import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { getAvailableOffers } from '../../../store/componentStores/Offer/offer.selectors';
import { showLoyalty } from '../../../store/reducers';
import { Box, Text } from '../../ui-kit';
import theme from '../../ui-kit/themes/theme';
import {
  editCopyByReplaceString,
  editCopyByReplaceWithComponent,
  editCopyByReplaceWithMultipleComponent
} from '../../utilities/copyFunctions';
import { LOYALTY_IMAGES } from '../assets/images';
import { BonusDealsLink, Icon, LoyaltyAvailableContainer } from '../assets/styles';
import * as COPY from '../utilities/copy';
import { emitEventsByName, EVENTS_DICTIONARY } from '../../../utilities/customEventsHelpers';

function earnCopySelection(loyaltyEarnThresholdNights = 2, numberOfNights, adventureBucks) {
  if (numberOfNights < loyaltyEarnThresholdNights) {
    return editCopyByReplaceString(COPY.EARN_POINTS, loyaltyEarnThresholdNights, '<THRESHOLD_NIGHTS>');
  }
  return adventureBucks > 0 ? COPY.SELECT_SUITE : COPY.START_EARNING;
}

function getLoyaltyCopy(availableOffers, isMobile) {
  if (isMobile) return COPY.YOU_HAVE_POINTS_BONUS_LINK_2_MOBILE;
  return availableOffers.length > 0 ? COPY.YOU_HAVE_POINTS_BONUS_LINK : COPY.YOU_HAVE;
}

function getBonusDealsCopy(availableOffers) {
  const availableOffersLength = availableOffers?.length;
  return availableOffersLength > 1 ? COPY.BONUS_DEALS : COPY.BONUS_DEAL;
}

const LoyaltyPointsAndText = ({ isNewWidgetStyleActive, copy }) => {
  return (
    <Text
      style={{ display: 'block ruby' }}
      fontSize={!isNewWidgetStyleActive ? 1 : 2}
      color={!isNewWidgetStyleActive ? theme.colors.pureWhite[0] : theme.colors.deepLakeBlue[100]}
      lineHeight={!isNewWidgetStyleActive ? 20 : 16}>
      {copy}
    </Text>
  );
};

const LoyaltyAvailable = ({
  isNewWidgetStyleActive,
  adventureBucks,
  iconSize,
  showLoyalty,
  loyaltyEarnThresholdNights = 2,
  numberOfNights = 0,
  isInsideBookingWidget = false,
  availableOffers
}) => {
  const isMobile = useMediaQuery({
    query: `(max-width: ${theme.breakpoints.sm})`
  });

  const bonusDealColor = isNewWidgetStyleActive => {
    if (isNewWidgetStyleActive) {
      return 'deepForestGreen.100';
    }

    if (!isNewWidgetStyleActive) {
      return 'voyagersVoyagersMoss';
    }

    return 'springGreen.100';
  };

  const bonusDealLink = (
    <BonusDealsLink
      color={bonusDealColor(isNewWidgetStyleActive)}
      onClick={() => {
        document.querySelector('[data-eventid="voyagers-club-offers"]')?.click();
        emitEventsByName(EVENTS_DICTIONARY.GWR_OPEN_MOBILE_NAV_DEALS);
      }}>
      {getBonusDealsCopy(availableOffers)}
    </BonusDealsLink>
  );

  const boldText = (
    <Text as={'b'}>{editCopyByReplaceString(COPY.VOYAGERS_POINTS_NO_EXCLAMATION, adventureBucks, '<POINTS>')}</Text>
  );

  const noPointsCopy = {
    '<TEXT>': boldText,
    '<SECOND_TEXT>':
      availableOffers.length > 0
        ? bonusDealLink
        : earnCopySelection(loyaltyEarnThresholdNights, numberOfNights, adventureBucks)
  };

  const withPointsCopy = {
    '<TEXT>': boldText,
    '<SECOND_TEXT>': availableOffers.length > 0 ? bonusDealLink : COPY.SELECT_SUITE
  };

  const textForMobileTest = {
    '<TEXT>': boldText,
    '<SECOND_TEXT>': bonusDealLink
  };

  const copyToUse = getLoyaltyCopy(availableOffers, isMobile);

  const LoyaltyContainer = (
    <LoyaltyAvailableContainer
      isNewWidgetStyleActive={isNewWidgetStyleActive}
      isMobile={isMobile}
      isInsideBookingWidget={isInsideBookingWidget}>
      <Icon
        alignSelf={'flex-start'}
        src={LOYALTY_IMAGES.VOYAGER_ICON_FOREST_GREEN_WHITE}
        alt="Loyalty"
        size={iconSize}
      />
      <Box ml={isMobile && !isInsideBookingWidget ? 3 : 2}>
        <Text
          as="p"
          my="0"
          fontSize={isMobile ? 1 : 0}
          fontWeight={theme.fontWeights.medium}
          color={!isNewWidgetStyleActive ? theme.colors.pureWhite[0] : theme.colors.deepLakeBlue[100]}>
          {isMobile ? (
            <>
              <Text
                mb={2}
                width={availableOffers.length > 0 ? '75%' : '100%'}
                fontWeight={theme.fontWeights.bold}
                fontSize={isNewWidgetStyleActive ? 0 : 1}
                color={!isNewWidgetStyleActive ? theme.colors.pureWhite[0] : theme.colors.deepLakeBlue[100]}
                lineHeight={!isNewWidgetStyleActive ? 20 : 16}>
                {availableOffers.length > 0
                  ? editCopyByReplaceWithMultipleComponent(copyToUse, textForMobileTest)
                  : editCopyByReplaceWithComponent(COPY.POINTS_AVAILABLE, boldText, '<POINTS>')}
              </Text>
              {adventureBucks === 0 ? (
                <Text
                  mt={0}
                  fontSize={0}
                  color={!isNewWidgetStyleActive ? theme.colors.pureWhite[0] : theme.colors.deepLakeBlue[100]}
                  fontWeight={theme.fontWeights.medium}
                  lineHeight={'150%'}>
                  {earnCopySelection(loyaltyEarnThresholdNights, numberOfNights, adventureBucks)}
                </Text>
              ) : (
                <Text
                  mt="1"
                  fontSize={0}
                  color={!isNewWidgetStyleActive ? theme.colors.pureWhite[0] : theme.colors.deepLakeBlue[100]}
                  fontWeight={theme.fontWeights.medium}
                  lineHeight={'150%'}>
                  {COPY.SELECT_SUITE}
                </Text>
              )}
            </>
          ) : (
            <>
              {adventureBucks === 0 ? (
                <LoyaltyPointsAndText
                  isNewWidgetStyleActive={isNewWidgetStyleActive}
                  copy={editCopyByReplaceWithMultipleComponent(copyToUse, noPointsCopy)}
                />
              ) : (
                <LoyaltyPointsAndText
                  isNewWidgetStyleActive={isNewWidgetStyleActive}
                  copy={editCopyByReplaceWithMultipleComponent(copyToUse, withPointsCopy)}
                />
              )}
            </>
          )}
        </Text>
      </Box>
    </LoyaltyAvailableContainer>
  );

  return <>{showLoyalty ? LoyaltyContainer : null}</>;
};
LoyaltyAvailable.propTypes = {
  adventureBucks: PropTypes.number,
  iconSize: PropTypes.string
};
LoyaltyAvailable.defaultProps = {
  adventureBucks: '153',
  iconSize: '32px'
};

const mapStateToProps = state => ({
  showLoyalty: showLoyalty(state),
  availableOffers: getAvailableOffers(state)
});

export default connect(mapStateToProps)(LoyaltyAvailable);
